import { path, join, reject, pipe, omit, isEmpty, isNil } from 'ramda'
import { useCallback, useEffect, useState, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation, Trans } from 'react-i18next'
import { Redirect, Link, NavLink } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'
import useMediaQuery from '@/hooks/use-media-query'
import { Breadcrumbs, BackButton } from '@/components/breadcrumbs'
import { Button } from '@/components/button'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import config from '@/config'
import { ChevronIcon, VerticalMenu } from '@/components/icons'
import useProfile from '@/hooks/use-profile'
import useRole from '@/hooks/use-role'
import useCurrentSession from '@/hooks/use-current-session'
import useBetaUser from '@/hooks/use-beta-user'
import useEventListener from '@/hooks/use-event-listener'
import { select } from '@/store'
import { breakpoints, fontWeights, colors } from '@/styles'
import { toast } from '@/components/toast-notifications'
import AreaTitle from '@/components/area-title'
import useGetSuiteInfo from '@/hooks/get-suite-info'
import useFinancialFiles from '@/hooks/use-financial-files'
import { isFalsy } from '@/utils'
import {
  BasicInfoView,
  ReferencesView,
  DocumentsView,
  TrustScoreView
} from '@/features/Application'
import { MessageApplication } from '@/components/icons'
import { TrustScoreLockedBlue, TrustScoreUnlockedBlue } from '@/components/icons'
import Skeleton from '@/components/skeleton'
import { showCopyApplicationModal } from '@/components/copy-application-modal'
import { useAvailableCreditChecks } from '@/hooks/use-block-and-pay'
import Highlight from './tour'
import { MobileMenu, BottomNavigation } from './menus'
import { DesktopMenu } from './desktop-menu'
import backArrow from './back-arrow.svg'
import Background from './application-background.svg'
import BackgroundMobile from './application-background-mobile.svg'
import { show as MobileMenuModal } from './modal'
import LeftContentComponent from './left-content-component'

const InnerContentMaxWidth = '900'

const StyledContentWrapper = styled.div`
  position: relative;
  width: 100%;
  margin: auto;
  ${({ flex = true }) =>
    flex &&
    `
  flex: 1;
  `}
  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    padding-top: 18px;
    max-width: none;
  }
`

const Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    align-items: unset;
    flex-direction: column;
  }
  > ${AreaTitle} + div {
    > div:first-of-type {
      height: 48px;
    }
  }
`

const Wrapper = styled(Page)`
  @media print {
    > div {
      display: flex;
      flex-direction: column;
      max-width: 900px;
    }

    h2 {
      font-size: 24px;
      font-weight: ${fontWeights.medium};
    }
  }

  margin-top: 40px;
  background-image: url(${Background});
  background-repeat: no-repeat;
  background-size: contain;
  background-color: ${colors.white};

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    background-color: ${colors.background};
    background-image: url(${BackgroundMobile});
  }
`

const ContentHeader = styled.div`
  width: 100%;
  min-height: 44px;
  z-index: 1;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    background: ${colors.white};
  }
`

const InnerContentHeader = styled.div`
  display: flex;
  justify-content: ${({ isMobile }) => (isMobile ? 'flex-end' : 'center')};
  align-items: center;
  margin: 0 auto;
  padding: 0 30px;
  min-height: 44px;
  background-color: ${colors.white};
  position: sticky;
  width: 100%;
  border-bottom: 1px solid ${colors.grey88};

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    justify-content: center;
    padding: 0;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    min-height: 44px;
    justify-content: space-between;
    align-items: flex-end;
  }
`

const BreadCrumbBackButton = styled(BackButton)`
  color: ${colors.secondary};
  :before {
    content: url(${backArrow});
    padding-right: 5px;
    height: 15px;
    width: 18px;
  }
`

const StyledBreadcrumbs = styled(Breadcrumbs)`
  margin-right: auto;
`

const InnerContent = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 285px) minmax(350px, 1fr);
  flex: 1;
  max-width: 1185px;
  margin: 40px;
  @media screen and (max-width: ${breakpoints.dashboardMobile}) {
    margin: 40px 20px;
  }
  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    display: grid;
    grid-template-columns: 1fr;
    margin: 0px 20px 20px;
  }
`

const CreateContractContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${({ disableMargin }) => (disableMargin ? 'unset' : '20px 24px auto 0')};
  padding: 20px;
  background-color: ${colors.white};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  height: auto;
  max-width: 900px;
`

const CreateContractTitle = styled.span`
  color: ${colors.black};
  font-weight: ${fontWeights.bold};
  font-size: 16px;
  line-height: 20px;
  margin-bottom: 24px;
`

const InnerContentRight = styled.div`
  z-index: 0;
  margin-left: 24px;
  margin-right: 0px;
  max-width: ${InnerContentMaxWidth}px;

  @media screen and (max-width: ${breakpoints.dashboardMobile}) {
    max-width: unset;
    margin-left: 30px;
  }
  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    margin: 0;
  }
`

const ChevronIconWrapper = styled.div`
  margin: 20px;
  font-size: 14px;
  line-height: 16px;
  color: ${colors.grey24};
  width: 100%;
  display: flex;
  max-width: 300px;
`

const ChevronContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
`

const ChevronIconStyled = styled(ChevronIcon)`
  transform: rotate(90deg);
  margin-right: 5px;
`

const TitleAddressWrapper = styled.div`
  color: ${colors.link};
`

const InnerContentRightNav = styled.div`
  width: 100%;
  display: flex;
`

const ChatMenuWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 10px;
  width: 56px;
  height: 56px;
  display: flex;
  padding: 10px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  background-color: ${colors.white};
  border: 1.5px solid ${colors.grey88};
  margin-left: 10px;
  box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
`

const Tabs = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: absolute;
`

const Tab = styled.div`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ active }) => (active ? colors.grey96 : colors.white)};
  width: 13vw;
  max-width: 180px;
  height: 60px;
  border-radius: 8px 8px 0px 0px;
  margin-right: 12px;
  border: 1px solid #e0e0e0;
  border-bottom: none;
  z-index: ${({ active }) => (active ? 1 : 0)};
  padding: ${({ active }) => (active ? '0 6px' : '0 12px')};

  @media screen and (max-width: 1390px) {
    width: ${({ sidebarExpanded }) => (sidebarExpanded ? '12vw' : '14vw')};
  }

  @media screen and (max-width: 1280px) {
    width: ${({ sidebarExpanded }) => (sidebarExpanded ? '10.5vw' : '14.3vw')};
    padding: ${({ sidebarExpanded }) => (sidebarExpanded ? '0 6px' : '0 12px')};
  }
`

const TabLabel = styled.span`
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 'bold' : 'normal')};
  display: flex;
  align-items: center;
  text-align: center;
  pointer-events: none;
`

const LockWrapper = styled.span`
  display: inline-block;
  margin-left: 8px;
  height: 16px;
  color: #3a82bb;
`

const SendMessageButton = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SendMessageIcon = styled(MessageApplication)`
  margin-right: 9px;
`

const ContractButton = ({ onClick, to, data, id, ...rest }) => {
  const extra =
    typeof onClick == 'function'
      ? { onClick }
      : {
          as: NavLink,
          to: {
            pathname: to,
            state: {
              scrollToTop: true,
              ...data
            }
          }
        }
  return <Button id={id} theme="primary" size="small" left={10} {...rest} {...extra} />
}

const MobileContractButton = styled(ContractButton)`
  ${props => props.border && `border: 0px !important; font-size: 14px; font-weight: 700;`}
  width: 100%;
`

export const ApplicationViewV2 = ({
  match: {
    params: { applicationId, timelineId }
  },
  history,
  location,
  sidebarExpanded
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const userRole = useRole()
  const myProfile = useProfile()
  const session = useCurrentSession()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)
  const [isExportOptionsExpanded, ,] = useState(false)
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const betaUser = useBetaUser()
  const betaUserContractLite = betaUser.custom_contract
  const isDev = config.BUILD_ENV === 'dev'
  const currentBetaUser = isDev ? true : betaUserContractLite.includes(parseInt(session.id))
  const { availableCreditChecks } = useAvailableCreditChecks()
  const hasUnlockCredits = availableCreditChecks >= 1
  const canCopyApplication = myProfile?.company_info?.registration_plan === 'enterprise'
  const application = useSelector(state =>
    select.application.applicationDetail(state, {
      applicationId
    })
  )

  const applicationLoading = !!application?.loading
  const version = application?.version

  const applicantId = application?.user_info?.id
  const listingId = application?.listing_id
  const listingData = useGetSuiteInfo(listingId)
  const submittedFilesCount = useFinancialFiles(
    application?.user_info?.files?.user_secure_files,
    userRole
  )?.length

  const { ref, inView } = useInView({
    threshold: 0
  })

  const map = useMemo(
    () => ({
      'trust-score': (
        <TrustScoreView
          hasUnlockCredits={hasUnlockCredits}
          application={application}
          applicationLoading={applicationLoading}
        />
      ),
      'basic-information': (
        <BasicInfoView application={application} applicationLoading={applicationLoading} />
      ),
      references: (
        <ReferencesView application={application} applicationLoading={applicationLoading} />
      ),
      documents: (
        <DocumentsView
          hasUnlockCredits={hasUnlockCredits}
          application={application}
          applicationLoading={applicationLoading}
        />
      )
    }),
    [applicationLoading, application, hasUnlockCredits]
  )

  const isLocked = application?.trust_score?.state === 'locked'
  const isLoading = !!application?.trust_score?.loading
  const pathname = location?.pathname
  const currentPath = pathname.split('/').pop()

  const pathWithoutQueryParamsExists = !!map?.[currentPath]

  const shouldRedirectToTrustScore = !pathWithoutQueryParamsExists

  const [isTrustScoreLoaded, setIsTrustScoreLoaded] = useState(false)

  const links = [
    {
      key: 'trust-score',
      param: null,
      label: t('l.listing.application.liv_score.text', 'Trust Score'),
      active: location.pathname.includes('trust-score') && isEmpty(location?.hash),
      icon: isLocked || isLoading ? <TrustScoreLockedBlue /> : <TrustScoreUnlockedBlue />,
      clickId: 'application-trust-score-sections-score'
    },
    {
      key: 'basic-information',
      param: null,
      label: t('r.profile.edit.basic_information.title', 'Basic Information'),
      active: location.pathname.includes('basic-information'),
      clickId: 'application-trust-score-sections-basic-info'
    },
    {
      key: 'references',
      param: null,
      label: t('r.profile.edit.references.tab', 'References'),
      active: location.pathname.includes('references'),
      clickId: 'application-trust-score-sections-reference'
    },
    {
      key: 'documents',
      param: null,
      label: t('l.contract.view.verified_documents.title', {
        defaultValue: 'Verified Documents ({{submittedFilesCount}})',
        submittedFilesCount
      }),
      active: location.pathname.includes('documents'),
      clickId: 'application-trust-score-sections-docs'
    }
  ]

  useEffect(() => {
    if (shouldRedirectToTrustScore) {
      const basePath = `${pathname.substring(
        0,
        pathname.lastIndexOf(`${applicationId}`)
      )}${applicationId}`
      history.replace(`${basePath}/trust-score`, { prev: location.state?.prev })
    }
  }, [shouldRedirectToTrustScore, applicationId, history, location, pathname])

  useEffect(() => {
    if (isDataLoaded) return
    const loadApplication = async () => {
      await dispatch.application.loadNoLoading(applicationId)
      setIsDataLoaded(true)
    }
    loadApplication()
  }, [applicationId, dispatch.application, isDataLoaded])

  useEffect(() => {
    if (isTrustScoreLoaded) return
    if (version !== '2') {
      dispatch.application.patch([applicationId, 'trust_score'], { loading: true })
      return
    }
    const loadApplication = async () => {
      await dispatch.application.getApplicationTrustScore(applicationId)
      setIsTrustScoreLoaded(true)
    }
    loadApplication()
  }, [applicationId, dispatch.application, isTrustScoreLoaded, version])

  useEffect(() => {
    if (application?.listing_id && userRole === 'landlord') {
      dispatch.listing.getContractNoLoading(application.listing_id)
    }
  }, [application?.listing_id, dispatch.listing, userRole])

  const currentComponents = useCallback(() => {
    if (currentPath in map) return map[currentPath]
    return map['trust-score']
  }, [currentPath, map])

  const formatAddress = (address, role) => {
    const unitHidden = !!Number(address.hide_unit_number) && role === 'tenant'
    const unit = address.unit_number && !unitHidden ? `${address.unit_number} - ` : ''
    const isApartment = address.building_type !== 'house'
    const rest = pipe(
      reject(isFalsy),
      join(', ')
    )([
      !unitHidden || isApartment ? address.full_street_name : address.street_name,
      address.city,
      address.state,
      address.zip?.toUpperCase()
    ])
    return `${unit}${rest}`
  }

  const applicationListingId = listingId || application?.listing_id

  const isBreadcrumbsDisplayed =
    (!!listingId || !!timelineId) && !(isExportOptionsExpanded && isMobile)

  const listingContract = useSelector(path(['listing', application?.listing_id, 'contract']))
  const contractNotLoaded = isNil(listingContract)
  const contractId = listingContract?.listing_contract_id

  const hasContract =
    listingContract?.listing_contract_id && listingContract?.state_machine !== 'draft'

  const isNative = listingContract?.contract_structure === 'native'
  const createdContractIsCurrentApplicant = listingContract?.tenant_ids?.includes(
    application?.user_info?.id
  )
  const sentContract = listingContract?.state_machine === 'sent'
  const signedContract =
    listingContract?.listing_contract_id &&
    (listingContract?.state_machine === 'active' || listingContract?.state_machine === 'signed')

  const markedAsRented = listingData?.marked_as_rented === '1'

  const hasValidContract =
    hasContract && listingContract?.tenant_ids?.includes(application?.user_info?.id)
  const wasAtListings = history.location.pathname.includes('/listings/')

  const getContractButtonDescription = useCallback(() => {
    if (contractNotLoaded) {
      return <Skeleton width="100%" height={10} />
    }
    if (signedContract) {
      return t('l.application.view.view_contract_title.title', 'The contract has been signed')
    }
    if (sentContract) {
      if (createdContractIsCurrentApplicant) {
        return t(
          'l.application.view.sent_contract__title.title',
          "You've sent this applicant the contract"
        )
      } else {
        return t(
          'l.application.view.sent_contract_diff_applicant_title.title',
          'A contract has been started for this listing'
        )
      }
    } else {
      return t(
        'l.application.view.create_contract_title.title',
        'Like what you see? Proceed with this applicant'
      )
    }
  }, [signedContract, sentContract, createdContractIsCurrentApplicant, contractNotLoaded])

  const applicationAvailable = !!application.id && !!application.listing_info

  const hideContract = !applicationAvailable || sentContract

  const showTourHighlight =
    !(userRole !== 'landlord' || hideContract || markedAsRented) &&
    (!hasContract || !hasValidContract)

  const getContractButton = useCallback(() => {
    const create = t('l.application.view.create_contract.button', 'Create Contract')
    const view = t('l.application.view.view_contract.button', 'View Contract')
    if (contractNotLoaded) {
      return <Skeleton width="100%" height={30} />
    }
    if (hasContract) {
      const contractUrl = isNative ? `/contract/${contractId}` : `/contract/manager/${contractId}`
      return isMobile ? (
        <MobileContractButton
          to={contractUrl}
          theme="secondary"
          size="small"
          width={145}
          height={40}
          padding="0"
          id="application-trust-score-sidebar-view-contract"
        >
          {view}
        </MobileContractButton>
      ) : (
        <ContractButton
          theme="secondary"
          id="application-trust-score-sidebar-view-contract"
          to={contractUrl}
        >
          {view}
        </ContractButton>
      )
    } else {
      const link = currentBetaUser
        ? `/listing/${applicationListingId}/contract/choose/types`
        : `/application/${applicationId}/contract/create`
      return isMobile ? (
        <MobileContractButton
          id="application-trust-score-sidebar-create-contract"
          to={link}
          data={{ applicantId: applicantId, applicationId }}
        >
          {create}
        </MobileContractButton>
      ) : (
        <ContractButton
          id="application-trust-score-sidebar-create-contract"
          to={link}
          data={{ applicantId: applicantId, applicationId }}
        >
          {create}
        </ContractButton>
      )
    }
  }, [
    contractId,
    hasContract,
    currentBetaUser,
    applicationListingId,
    applicationId,
    applicantId,
    isMobile,
    contractNotLoaded,
    t
  ])

  const navigateChat = () => {
    history.push(`/chat/${application?.timeline_room}`)
  }

  const getMessageButton = () => {
    const msg = t('l.application.view.send_message.button', 'Chat Now')

    if (contractNotLoaded) {
      return <Skeleton width="100%" height={30} />
    }

    return (
      <SendMessageButton>
        <Button
          id="application-trust-score-sidebar-send-mssg"
          size="small"
          theme="secondary"
          width={'100%'}
          height={40}
          onClick={navigateChat}
        >
          <SendMessageIcon />
          {msg}
        </Button>
      </SendMessageButton>
    )
  }

  const ApplicationBreadcrumbs = useCallback(() => {
    const applicantFirstName = `${application?.user_info?.first_name}`
    const label = applicationLoading ? (
      <Skeleton width={115} height={20} />
    ) : (
      <Trans
        i18nKey="l.application.view.users_application.link"
        applicantFirstName={applicantFirstName}
      >
        {{ applicantFirstName }}'s Application
      </Trans>
    )
    const breadcrumbs = wasAtListings
      ? [
          {
            to: '/listings',
            label: t('l.listing.view.all_listings.link', 'All Listings')
          },
          {
            to: `/listing/${listingId}`,
            label: t('l.listing.view.dashboard.link', 'Dashboard')
          },
          {
            to: `/listing/${listingId}/applications`,
            label: t('l.listing.view.listing_dashboard_applications.title', 'Applications')
          },
          {
            to: null,
            label: label
          }
        ]
      : [
          {
            to: `/chat/${timelineId}`,
            label: t('common.chat', 'Chat')
          },
          {
            to: null,
            label: label
          }
        ]
    return <StyledBreadcrumbs items={breadcrumbs} CustomBackButton={BreadCrumbBackButton} />
  }, [
    wasAtListings,
    application?.user_info?.first_name,
    listingId,
    t,
    timelineId,
    applicationLoading
  ])

  const backUrl = listingId ? `/listings/${listingId}/applications` : `/chat/${timelineId}`

  const handleNavLinkClick = key => {
    const isKeySame = location.pathname.includes(key)
    const url = `${pathname?.substring(0, pathname?.lastIndexOf('/'))}/${key}`
    const navState = `${key}`
    if (!isKeySame || (key === 'trust-score' && !isEmpty(location?.state))) {
      history.push(url, navState)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      return
    }
  }

  const offset = 50
  const onScroll = () =>
    window.pageYOffset < offset ? setIsScrolling(false) : setIsScrolling(true)

  useEventListener('scroll', onScroll)

  useEffect(() => {
    if (!isDataLoaded || applicationAvailable || applicationLoading) return
    toast(
      t(
        'b.application.view.tenancy_application_not_available.text',
        'This application is no longer available.'
      ),
      {
        title: t(
          'b.application.view.tenancy_application_not_available.title',
          'Application Withdrawn'
        ),
        type: 'warning'
      }
    )
  }, [applicationAvailable, isDataLoaded, t, applicationLoading])

  if (!applicationAvailable && isDataLoaded && !applicationLoading) return <Redirect to={backUrl} />

  return (
    <Wrapper inView={inView}>
      <StyledContentWrapper className="mb-50">
        <Helmet>
          <title>{t('b.application.view.tenancy_application.title', 'Tenancy Application')}</title>
        </Helmet>
        <PageTitle
          customChild={
            userRole === 'landlord' && (
              <ContentHeader>
                <InnerContentHeader isMobile={isMobile}>
                  {isBreadcrumbsDisplayed && !isMobile && !isScrolling && (
                    <ApplicationBreadcrumbs />
                  )}
                  {isMobile ? (
                    <MobileMenu
                      isLocked={isLocked}
                      location={location}
                      handleNavLinkClick={handleNavLinkClick}
                    />
                  ) : !isMobile && isScrolling ? (
                    <DesktopMenu
                      isLocked={isLocked}
                      location={location}
                      handleNavLinkClick={handleNavLinkClick}
                      isScrolling={!isMobile && isScrolling}
                      submittedFilesCount={submittedFilesCount}
                    />
                  ) : null}
                </InnerContentHeader>
              </ContentHeader>
            )
          }
        >
          {!!listingData ? (
            <Link
              to={{ pathname: `/listing/${listingId}` }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <TitleAddressWrapper>
                {formatAddress(omit(['zip', 'state', 'city'], listingData))}
              </TitleAddressWrapper>
            </Link>
          ) : (
            <Skeleton width={300} height={20} />
          )}
        </PageTitle>
        <Inner>
          {isMobile && (
            <>
              <ChevronIconWrapper>
                <ChevronContainer
                  onClick={() =>
                    history.push(
                      wasAtListings ? `/listing/${listingId}/applications` : `/chat/${timelineId}`
                    )
                  }
                >
                  <ChevronIconStyled />{' '}
                  {wasAtListings
                    ? t('l.listing.view.listing_dashboard_applications.title', 'Applications')
                    : t('common.chat', 'Chat')}
                </ChevronContainer>
              </ChevronIconWrapper>
            </>
          )}
          <InnerContent>
            {((currentPath === 'trust-score' && isMobile) || !isMobile) && (
              <LeftContentComponent
                application={application}
                history={history}
                canCopyApplication={canCopyApplication}
                getContractButton={getContractButton}
                getContractButtonDescription={getContractButtonDescription}
                getMessageButton={getMessageButton}
                buttonRef={ref}
                applicationLoading={applicationLoading}
              />
            )}

            <InnerContentRight>
              {!isMobile && (
                <Tabs>
                  {links.map(link => {
                    return (
                      <Tab
                        id={link.clickId}
                        key={link.key}
                        active={link.active}
                        onClick={() => handleNavLinkClick(link.key)}
                        sidebarExpanded={sidebarExpanded}
                      >
                        <TabLabel active={link.active}>
                          {link.label}
                          {link.icon && <LockWrapper>{link.icon}</LockWrapper>}
                        </TabLabel>
                      </Tab>
                    )
                  })}
                </Tabs>
              )}
              {/* Place for each sections */}
              {currentComponents()}
              {isMobile && currentPath === 'documents' && (
                <CreateContractContainer disableMargin>
                  <CreateContractTitle>{getContractButtonDescription()}</CreateContractTitle>
                  {getContractButton()}
                </CreateContractContainer>
              )}
              <InnerContentRightNav>
                <BottomNavigation
                  handleNavLinkClick={handleNavLinkClick}
                  getContractButton={getContractButton}
                />
              </InnerContentRightNav>
              {isMobile && !inView && (
                <ChatMenuWrapper
                  onClick={() => {
                    MobileMenuModal({
                      application,
                      history,
                      canCopyApplication,
                      showCopyApplicationModal,
                      getContractButton,
                      preferredName: application?.user_info?.preferred_name
                    })
                  }}
                >
                  <VerticalMenu />
                </ChatMenuWrapper>
              )}
            </InnerContentRight>
          </InnerContent>
        </Inner>
      </StyledContentWrapper>
      {showTourHighlight && <Highlight />}
    </Wrapper>
  )
}

export default ApplicationViewV2
